import type { SessionScope } from './scope';

/**
 * What survives a page reload. The access token is never persisted: it lives
 * in memory only and is re-minted on load.
 *  - body transport (deprecated): the refresh token itself is kept here
 *  - cookie transport: no token at all; the refresh token is an httpOnly
 *    cookie the script can't read, and only the scope is remembered
 */
export type StoredSession = BodyStoredSession | CookieStoredSession;

export interface BodyStoredSession {
  transport?: undefined;
  refreshToken: string;
  scope: SessionScope;
  /** The agency-level session, kept while working inside a sub-account. */
  homeRefreshToken?: string;
}

export interface CookieStoredSession {
  transport: 'cookie';
  refreshToken?: undefined;
  homeRefreshToken?: undefined;
  scope: SessionScope;
  /** True while inside a sub-account with the agency-level session kept. */
  homeSession?: boolean;
}

export interface SessionStore {
  load(): StoredSession | null;
  save(session: StoredSession | null): void;
}

export function memorySessionStore(initial: StoredSession | null = null): SessionStore {
  let value = initial;
  return {
    load: () => value,
    save: (session) => {
      value = session;
    },
  };
}

function isStoredSession(value: unknown): value is StoredSession {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  const scope = record.scope as Record<string, unknown> | undefined;
  const validScope = typeof scope === 'object' && scope !== null && (scope.kind === 'home' || scope.kind === 'subaccount');
  if (!validScope) return false;
  if (record.transport === 'cookie') {
    return record.refreshToken === undefined && (record.homeSession === undefined || typeof record.homeSession === 'boolean');
  }
  return (
    record.transport === undefined &&
    typeof record.refreshToken === 'string' &&
    (record.homeRefreshToken === undefined || typeof record.homeRefreshToken === 'string')
  );
}

/**
 * Persists the session in Web Storage (sessionStorage by default: one tab,
 * cleared when it closes). With the cookie transport (what the app uses) that
 * is only the scope; with the deprecated body transport it includes the
 * refresh token. See apps/web/README.md.
 */
export function webSessionStore(storage: Storage, key = 'saas-web.session'): SessionStore {
  return {
    load() {
      let raw: string | null;
      try {
        raw = storage.getItem(key);
      } catch (err) {
        console.warn('[session-store] storage unavailable; starting signed out', err);
        return null;
      }
      if (raw === null) return null;
      try {
        const parsed: unknown = JSON.parse(raw);
        if (isStoredSession(parsed)) return parsed;
        console.warn('[session-store] discarding stored session with an unexpected shape');
      } catch (err) {
        console.warn('[session-store] discarding unreadable stored session', err);
      }
      storage.removeItem(key);
      return null;
    },
    save(session) {
      try {
        if (session) storage.setItem(key, JSON.stringify(session));
        else storage.removeItem(key);
      } catch (err) {
        console.warn('[session-store] could not persist session; it will not survive a reload', err);
      }
    },
  };
}
